import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import Nav from "@/components/Nav";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Download, Sparkles, BookHeart } from "lucide-react";
import { http, API } from "@/lib/api";

const STAGES = [
  { pct: 5, msg: "Warming up the ink…" },
  { pct: 20, msg: "Writing the opening chapter…" },
  { pct: 40, msg: "Sketching your character…" },
  { pct: 60, msg: "Painting watercolor scenes…" },
  { pct: 80, msg: "Threading pages together…" },
  { pct: 95, msg: "Binding your storybook…" },
  { pct: 100, msg: "Done — your book is ready!" },
];

function ProgressView({ status }) {
  const progress = status?.progress || 0;
  const stage = STAGES.find((s) => progress <= s.pct) || STAGES[STAGES.length - 1];
  return (
    <div className="text-center" data-testid="progress-screen">
      <div className="inline-flex gap-2 justify-center mb-6">
        {["a", "b", "c", "d", "e"].map((k, i) => (
          <span key={k} className="twinkle text-[#F2CC8F] text-3xl" style={{ animationDelay: `${i * 0.3}s` }}>✦</span>
        ))}
      </div>
      <h1 className="font-display text-4xl sm:text-5xl font-black leading-tight">
        We're painting {status?.title ? `"${status.title}"` : "your storybook"}…
      </h1>
      <p className="mt-4 text-[#F2CC8F]/80 text-lg">This usually takes 60–120 seconds. Grab a coffee ☕</p>
      <div className="mt-10 paper-card !bg-white/5 !border-white/20 !text-white p-8">
        <div className="text-sm uppercase tracking-widest text-[#F2CC8F]">Now working on</div>
        <div className="font-display text-2xl font-bold mt-1" data-testid="progress-stage">{stage.msg}</div>
        <div className="mt-6">
          <Progress value={progress} className="h-3 bg-white/10" data-testid="progress-bar" />
          <div className="mt-2 text-right text-sm text-white/70">{progress}%</div>
        </div>
        <div className="mt-6 h-1.5 rounded-full shimmer opacity-70" />
      </div>
      <p className="mt-6 text-xs text-white/60">Bedtime tip: leave your device on — we'll also email the link.</p>
    </div>
  );
}

function ReadyView({ status }) {
  return (
    <div className="text-center" data-testid="ready-screen">
      <div className="text-6xl mb-4">🎉</div>
      <h1 className="font-display text-4xl sm:text-5xl font-black">Your book is ready!</h1>
      <p className="mt-4 text-[#F2CC8F]/80 text-lg">"{status.title}" — delivered with love.</p>
      <div className="mt-10 paper-card !bg-white !text-[#2B2D42] p-8 text-center">
        <BookHeart className="mx-auto text-[#E07A5F] mb-3" size={42} />
        <div className="font-display text-2xl font-bold">{status.title}</div>
        <div className="text-sm text-[#5C6370] mt-1">8 illustrated pages · PDF</div>
        <a href={`${API}/download/${status.order_id}`} target="_blank" rel="noreferrer" data-testid="download-btn">
          <Button className="mt-6 bg-[#E07A5F] hover:bg-[#D96B43] text-white rounded-full py-6 px-8 text-base">
            <Download size={16} className="mr-2" /> Download my PDF
          </Button>
        </a>
        <div className="mt-6 flex items-center justify-center gap-4 text-sm text-[#5C6370]">
          <Link to="/library" className="hover:text-[#E07A5F]">Save to my library</Link>
          <span>·</span>
          <Link to="/create" className="hover:text-[#E07A5F]"><Sparkles size={12} className="inline" /> Make another</Link>
        </div>
      </div>
    </div>
  );
}

export default function Success() {
  const [sp] = useSearchParams();
  const sessionId = sp.get("session_id");
  const [status, setStatus] = useState(null);
  const pollRef = useRef(null);

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await http.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        setStatus(data);
        if (data.pdf_ready && pollRef.current) clearInterval(pollRef.current);
      } catch (e) {
        console.warn("Status poll failed:", e?.response?.data || e?.message);
      }
    };
    tick();
    pollRef.current = setInterval(tick, 3000);
    return () => { cancelled = true; if (pollRef.current) clearInterval(pollRef.current); };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1D2A44] via-[#3D2C4D] to-[#2B1B38] text-white">
      <Nav />
      <main className="max-w-3xl mx-auto px-5 py-14">
        {!status?.pdf_ready ? <ProgressView status={status} /> : <ReadyView status={status} />}
      </main>
    </div>
  );
}
